<!-- src/components/ParticipantCard.vue -->
<!--
  Per-participant card used in the Case Detail view.

  Renders:
    - Role icon (defendant or indemnitor) + name + role label
    - Contact info row (phone, email)
    - Status indicators (consent, form completion, docs signed)
    - Contextual action buttons that vary by state:
        no contact info       → just shows what's missing
        contact info, not yet → "Send magic link"
        invited, not consented → "Re-send link"
        filling forms          → "View form"
        forms 'enough'         → "OK to sign" + "View form"
        signing                → "View form" + "View signed docs"
        all signed             → "View signed docs"

  Emits semantic events. The parent decides what each one actually does.
-->

<template>
  <v-card flat border class="participant-card">

    <!-- Top row: icon + name + role label + remove (indemnitors only) -->
    <div class="participant-top">
      <v-icon
        size="22"
        :class="['role-icon', roleIconClass]"
      >
        {{ roleIcon }}
      </v-icon>
      <div class="participant-identity">
        <div class="participant-name">
          {{ displayName || nameFallback }}
          <span class="participant-role">{{ roleLabel }}{{ roleSuffix }}</span>
        </div>
      </div>
      <v-spacer />
      <v-btn
        v-if="role === 'indemnitor'"
        variant="text"
        size="x-small"
        color="grey-darken-1"
        @click="$emit('remove')"
      >
        <v-icon start size="14">mdi-close</v-icon>
        Remove
      </v-btn>
    </div>

    <!-- Status indicators -->
    <!-- (Contact info — phone, email — now lives on the dashboard row
         header alongside the participant name, so it isn't duplicated
         here.) -->
    <div class="participant-status">

      <!-- Consent -->
      <div class="status-indicator">
        <v-icon
          size="16"
          :color="!!participant.consent_id ? 'green-darken-1' : 'grey-lighten-1'"
        >
          {{ !!participant.consent_id ? 'mdi-check-circle' : 'mdi-circle-outline' }}
        </v-icon>
        <span :class="{ 'status-complete': !!participant.consent_id }">
          Consented
        </span>
      </div>

      <!-- Form sections -->
      <div v-if="participant.sectionsTotal !== undefined" class="status-indicator">
        <v-icon
          size="16"
          :color="sectionsIconColor"
        >
          {{ sectionsIcon }}
        </v-icon>
        <span :class="{ 'status-complete': allSectionsDone }">
          <span class="status-detail">
            {{ participant.sectionsComplete }}/{{ participant.sectionsTotal }}
          </span>
          sections
        </span>
      </div>

      <!-- Form fields -->
      <div v-if="participant.fieldsTotal !== undefined" class="status-indicator">
        <v-icon
          size="16"
          :color="formIconColor"
        >
          {{ formIcon }}
        </v-icon>
        <span :class="{ 'status-complete': isFormComplete }">
          <span class="status-detail">
            {{ participant.fieldsComplete }}/{{ participant.fieldsTotal }}
          </span>
          fields
        </span>
      </div>

      <!-- Signatures -->
      <div v-if="hasDocumentsToSign" class="status-indicator">
        <v-icon
          size="16"
          :color="docsIconColor"
        >
          {{ docsIcon }}
        </v-icon>
        <span :class="{ 'status-complete': allDocsSigned }">
          Signed
          <span class="status-detail">
            {{ participant.documentsSigned }}/{{ participant.documentsTotal }}
          </span>
        </span>
      </div>

    </div>

    <!-- Action row -->
    <div class="participant-actions">
      <template v-for="action in availableActions" :key="action.id">
        <v-btn
          :variant="action.primary ? 'flat' : 'outlined'"
          :color="action.color || '#635347'"
          size="small"
          @click="$emit(action.event)"
        >
          <v-icon v-if="action.icon" start size="16">{{ action.icon }}</v-icon>
          {{ action.label }}
        </v-btn>
      </template>
    </div>

  </v-card>
</template>


<script>
export default {
  name: 'ParticipantCard',

  props: {
    participant: { type: Object, required: true },
    role: { type: String, required: true }, // 'defendant' or 'indemnitor'
    roleSuffix: { type: String, default: '' },
  },

  emits: ['send-link-sms', 'send-link-email', 'view-form', 'ok-to-sign', 'view-docs', 'remove'],

  computed: {
    displayName() {
      const p = this.participant;
      const id = p.identity || p;
      return [id.first_name || id.firstName, id.last_name || id.lastName]
        .filter(Boolean).join(' ');
    },

    nameFallback() {
      return this.role === 'defendant'
        ? 'No defendant name'
        : 'Unnamed indemnitor';
    },

    roleIcon() {
      return this.role === 'defendant'
        ? 'mdi-account-circle'
        : 'mdi-shield-account';
    },

    roleIconClass() {
      return this.role === 'defendant'
        ? 'role-icon-defendant'
        : 'role-icon-indemnitor';
    },

    roleLabel() {
      return this.role === 'defendant' ? 'Defendant' : 'Indemnitor';
    },

    // Form completion expressed as a 0-100 number derived from the
    // fields-complete / fields-total pair. Useful for thresholding
    // (e.g., show "OK to sign" once form is ≥75%).
    formCompletion() {
      const p = this.participant;
      if (p.fieldsTotal === undefined || p.fieldsTotal === 0) return undefined;
      return Math.round((p.fieldsComplete / p.fieldsTotal) * 100);
    },

    isFormComplete() {
      return this.formCompletion >= 100;
    },

    formIcon() {
      if (this.formCompletion === undefined) return 'mdi-circle-outline';
      if (this.formCompletion >= 100) return 'mdi-check-circle';
      if (this.formCompletion > 0) return 'mdi-progress-clock';
      return 'mdi-circle-outline';
    },

    formIconColor() {
      if (this.formCompletion === undefined) return 'grey-lighten-1';
      if (this.formCompletion >= 100) return 'green-darken-1';
      if (this.formCompletion > 0) return 'blue';
      return 'grey-lighten-1';
    },

    hasDocumentsToSign() {
      return this.participant.documentsTotal !== undefined
          && this.participant.documentsTotal > 0;
    },

    // Sections-complete indicators (separate from fields-complete because
    // "1 of 9 sections" reads more meaningful to the agent than the
    // raw field count).
    allSectionsDone() {
      const p = this.participant;
      return p.sectionsTotal !== undefined
          && p.sectionsComplete === p.sectionsTotal;
    },

    sectionsIcon() {
      if (this.allSectionsDone) return 'mdi-check-circle';
      if (this.participant.sectionsComplete > 0) return 'mdi-progress-clock';
      return 'mdi-circle-outline';
    },

    sectionsIconColor() {
      if (this.allSectionsDone) return 'green-darken-1';
      if (this.participant.sectionsComplete > 0) return 'blue';
      return 'grey-lighten-1';
    },

    allDocsSigned() {
      return this.participant.documentsSigned === this.participant.documentsTotal;
    },

    docsIcon() {
      if (this.allDocsSigned) return 'mdi-check-circle';
      if (this.participant.documentsSigned > 0) return 'mdi-progress-clock';
      return 'mdi-circle-outline';
    },

    docsIconColor() {
      if (this.allDocsSigned) return 'green-darken-1';
      if (this.participant.documentsSigned > 0) return 'blue';
      return 'grey-lighten-1';
    },

    // Contextual action buttons based on the participant's 7-state
    // status. Returns an array of action descriptors.
    availableActions() {
      const p = this.participant;
      const actions = [];
      const hasPhone = !!(p.identity?.phone);
      const hasEmail = !!(p.identity?.email);
      const hasContact = hasPhone || hasEmail;

      if (!hasContact) {
        // Always show View form even without contact info.
        actions.push({
          id: 'view_form', event: 'view-form',
          label: 'View form',
          icon: 'mdi-form-textbox', primary: false,
        });
        return actions;
      }

      switch (p.status) {
        case 'awaiting_invite':
        case 'invited':
          if (hasPhone) {
            actions.push({
              id: 'send_link_sms', event: 'send-link-sms',
              label: 'Send Link',
              icon: 'mdi-phone-outline', primary: false,
            });
          }
          if (hasEmail) {
            actions.push({
              id: 'send_link_email', event: 'send-link-email',
              label: 'Send Link',
              icon: 'mdi-email-outline', primary: false,
            });
          }
          break;

        case 'filling':
          if (this.formCompletion !== undefined && this.formCompletion >= 75) {
            actions.push({
              id: 'ok_to_sign', event: 'ok-to-sign',
              label: 'OK to sign',
              icon: 'mdi-check-decagram-outline', primary: true,
            });
          }
          break;

        case 'submitted':
          actions.push({
            id: 'ok_to_sign', event: 'ok-to-sign',
            label: 'OK to sign',
            icon: 'mdi-check-decagram-outline', primary: true,
          });
          break;

        case 'ready_to_sign':
        case 'signed':
          if (this.hasDocumentsToSign) {
            actions.push({
              id: 'view_docs', event: 'view-docs',
              label: 'View signed docs',
              icon: 'mdi-file-sign', primary: p.status === 'signed',
            });
          }
          break;

        default:
          break;
      }

      // View form is always available.
      actions.push({
        id: 'view_form', event: 'view-form',
        label: 'View form',
        icon: 'mdi-form-textbox', primary: false,
      });

      return actions;
    },
  },
};
</script>


<style scoped>
.participant-card {
  padding: 16px;
  margin-bottom: 12px;
  background-color: #fff;
}

.participant-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.role-icon-defendant {
  color: #635347;
}
.role-icon-indemnitor {
  color: #d2b48c;
}
.participant-identity {
  flex: 1 1 0;
  min-width: 0;
}
.participant-name {
  font-size: 16px;
  font-weight: 500;
  color: #222;
  line-height: 1.2;
}
.participant-role {
  font-size: 12px;
  color: #888;
  font-weight: normal;
  margin-left: 8px;
}

.participant-status {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #888;
  margin-bottom: 12px;
  padding-left: 34px;
}
.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.status-complete {
  color: #222;
  font-weight: 500;
}
.status-detail {
  margin-left: 4px;
  font-weight: 500;
  color: #555;
}

.participant-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding-left: 34px;
}

@media (max-width: 768px) {
  .participant-status,
  .participant-actions {
    padding-left: 0;
  }
}
</style>
